import axios from 'axios';

export type ProductEndpointsType =
  | 'auth'
  | 'balance'
  | 'transactions'
  | 'identity'
  | 'identitymatch'
  | 'assets'
  | 'income_verification'
  | 'investments'
  | 'international_base_report'
  | 'international_income_insights';

export const PRODUCT_ENDPOINT_OPTIONS = [
  {
    label: 'Auth',
    value: 'auth',
  },
  {
    label: 'Balance',
    value: 'balance',
  },
  {
    label: 'Transactions',
    value: 'transactions',
  },
  {
    label: 'Identity',
    value: 'identity',
  },
  {
    label: 'Financial Insights Report',
    value: 'assets',
  },
  {
    label: 'Income',
    value: 'income_verification',
  },
  {
    label: 'Investments (Alpha)',
    value: 'investments',
  },
  {
    label: 'Identity Match',
    value: 'identity',
  },
  {
    label: 'International Base Report',
    value: 'international_base_report',
  },
  {
    label: 'International Income Insights',
    value: 'international_income_insights',
  },
];

// Add a type for the common options
interface ProductEndpointOptions {
  transactionDays?: number;
  userFullName?: string;
  pfcVersion?: string;
}

// Update all endpoint functions to accept options
const authEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/auth`,
    {},
    { withCredentials: true },
  );
  return response.data;
};

const incomeEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/income`,
    {},
    { withCredentials: true },
  );
  return response.data;
};

const investmentsEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/investments`,
    {},
    { withCredentials: true },
  );
  return response.data.error != null
    ? 'An error occurred while fetching investments product'
    : response.data;
};

const balanceEndpoint = async () => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/balance`,
    {},
    {
      withCredentials: true,
    },
  );
  return response.data;
};

const PRODUCT_NOT_READY_RETRY_TIMEOUT = 4 * 60 * 1000;
const transactionsEndpoint = async (options?: ProductEndpointOptions) => {
  let data;

  const wait = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const retryTransactionsUntil = async (retryUntil: number): Promise<any> => {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/token/transactions`,
      { pfc_version: options?.pfcVersion },
      { withCredentials: true },
    );

    if (
      response.data.transactions_update_status !==
        'HISTORICAL_UPDATE_COMPLETE' &&
      retryUntil > Date.now()
    ) {
      // If the user just linked their account,
      // transaction data isn't available yet.
      // In this case we want to poll the server for some time
      // instead of making the user to refresh the page themselves.
      await wait(500);
      return retryTransactionsUntil(retryUntil);
    }

    return response.data;
  };

  data = await retryTransactionsUntil(
    Date.now() + PRODUCT_NOT_READY_RETRY_TIMEOUT,
  );
  console.info('transactions.data - Fetched transactions COMPLETE ', data);
  return data;
};

const identityEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/identity`,
    {},
    { withCredentials: true },
  );
  return response.data.error != null
    ? 'Identity is not supported'
    : response.data;
};

const assetsEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/assets/get`,
    {
      days_requested: options?.transactionDays ?? 90,
    },
    {
      withCredentials: true,
    },
  );

  if (response.data.error != null) {
    return 'An error occurred while fetching assets product';
  }
  return response.data;
};

const assetsCreateReportEndpoint = async (daysRequested?: number) => {
  await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/assets/create`,
    {
      days_requested: daysRequested ?? 90,
    },
    {
      withCredentials: true,
    },
  );
};

const identityMatchEndpoint = async (options?: ProductEndpointOptions) => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/token/identity/match`,
    {
      identityMatchName: options?.userFullName,
    },
    { withCredentials: true },
  );
  return response.data.error != null
    ? 'An error occurred while fetching identity-match product'
    : response.data;
};

const internationalBaseReportEndpoint = async (
  options?: ProductEndpointOptions,
) => {
  const wait = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const retryUntil = Date.now() + PRODUCT_NOT_READY_RETRY_TIMEOUT;

  const poll = async (): Promise<any> => {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/token/international_base_report`,
      {},
      { withCredentials: true },
    );

    if (response.data.error != null && retryUntil > Date.now()) {
      await wait(3000);
      return poll();
    }

    if (response.data.error != null) {
      return 'An error occurred while fetching international base report';
    }
    return response.data;
  };

  return poll();
};

const internationalIncomeInsightsEndpoint = async (
  options?: ProductEndpointOptions,
) => {
  const wait = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const retryUntil = Date.now() + PRODUCT_NOT_READY_RETRY_TIMEOUT;

  const poll = async (): Promise<any> => {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/token/international_income_insights`,
      {},
      { withCredentials: true },
    );

    if (response.data.error != null && retryUntil > Date.now()) {
      await wait(3000);
      return poll();
    }

    if (response.data.error != null) {
      return 'An error occurred while fetching international income insights';
    }
    return response.data;
  };

  return poll();
};

export const productEndpoints = {
  auth: authEndpoint,
  balance: balanceEndpoint,
  transactions: transactionsEndpoint,
  identity: identityEndpoint,
  assets: assetsEndpoint,
  income_verification: incomeEndpoint,
  investments: investmentsEndpoint,
  identitymatch: identityMatchEndpoint,
  international_base_report: internationalBaseReportEndpoint,
  international_income_insights: internationalIncomeInsightsEndpoint,
  // not an official Plaid product
  assets_create_report: assetsCreateReportEndpoint,
};
